import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react'

import axios, { AxiosError } from 'axios'
import { useHistory, useLocation } from 'react-router-dom'

import { identifyUser } from '../analytics'
import {
  introspect,
  refreshAuthToken,
  validate,
  permissions,
} from '../api/auth'
import {
  resolveAuthReturnPath,
  saveAuthReturnPath,
} from '../utils/authReturnPath'
import { createSessionManager } from '../utils/sessionManager'

import { useNotifications } from './notificationProvider'

let loginExpiredNotificationSent = false

const initialState = {
  email: null,
  token: null,
  firstName: null,
  last_name: null,
  public_id: null,
  isAuthenticated: false,
  permissions: null,
  loading: true,
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper context type
const UserContext = createContext<any>(null)

const UPDATE_USER = 'UPDATE_USER'
const RESET_USER = 'RESET_USER'
const LOGIN_USER = 'LOGIN_USER'

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper state and action types
const userReducer = (state: any, action: any) => {
  switch (action.type) {
    case LOGIN_USER:
      return {
        ...state,
        ...action.payload,
      }
    case UPDATE_USER:
      return {
        ...state,
        ...action.payload.item,
      }
    case RESET_USER: {
      const reset = { ...initialState, loading: false }
      return reset
    }
    default:
      return state
  }
}

const clearStoredSession = () => {
  localStorage.removeItem('db_usr')
  delete axios.defaults.headers.common['Authorization']
}

const persistSessionUser = (
  user: Record<string, unknown> & { token: string },
) => {
  localStorage.setItem('db_usr', JSON.stringify(user))
  axios.defaults.headers.common['Authorization'] = `Bearer ${user.token}`
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper children type
export const UserProvider = ({ children }: any) => {
  const history = useHistory()
  const location = useLocation<{
    from?: { pathname: string; search?: string; hash?: string }
  }>()
  const { state: locationState } = location
  const [state, dispatch] = useReducer(userReducer, initialState)
  const stateRef = useRef(state)
  stateRef.current = state

  const { addNotification } = useNotifications()
  const sessionManagerRef = useRef<ReturnType<
    typeof createSessionManager
  > | null>(null)

  const forceSessionExpired = useCallback(() => {
    clearStoredSession()
    if (!loginExpiredNotificationSent) {
      addNotification('Login expired', 'warning')
      loginExpiredNotificationSent = true
    }
    dispatch({
      type: 'UPDATE_USER',
      payload: {
        item: { token: null, isAuthenticated: false, loading: false },
      },
    })
    saveAuthReturnPath({
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
    })
    if (!window.location.pathname.includes('/login')) {
      history.push('/login')
    }
  }, [addNotification, history])

  useEffect(() => {
    const manager = createSessionManager({
      getToken: () => stateRef.current.token,
      isEnabled: () =>
        !!stateRef.current.isAuthenticated && !!stateRef.current.token,
      onExpired: forceSessionExpired,
      onRefreshed: (token, user) => {
        const persisted = {
          ...user,
          token,
        } as { token: string } & Record<string, unknown>
        persistSessionUser(persisted)
        loginExpiredNotificationSent = false
        dispatch({
          type: 'UPDATE_USER',
          payload: {
            item: {
              ...persisted,
              isAuthenticated: true,
              permissions: stateRef.current.permissions,
              loading: false,
            },
          },
        })
      },
      refreshToken: async () => {
        const refreshed = await refreshAuthToken()
        return {
          token: refreshed.token,
          user: refreshed as unknown as Record<string, unknown>,
        }
      },
    })
    sessionManagerRef.current = manager
    return () => {
      manager.stop()
      sessionManagerRef.current = null
    }
  }, [forceSessionExpired])

  useEffect(() => {
    const manager = sessionManagerRef.current
    if (!manager) {
      return
    }
    if (state.isAuthenticated && state.token) {
      manager.start()
      manager.notifyTokenChanged()
    } else {
      manager.stop()
    }
  }, [state.isAuthenticated, state.token])

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(config => {
      const manager = sessionManagerRef.current
      if (manager && !manager.assertValidForRequest()) {
        return Promise.reject(new axios.Cancel('Session expired'))
      }
      return config
    })

    const responseInterceptor = axios.interceptors.response.use(
      response => {
        return response
      },
      (error: AxiosError) => {
        if (error.response && error.response.status === 401) {
          forceSessionExpired()
        }

        return Promise.reject(error)
      },
    )

    return () => {
      axios.interceptors.request.eject(requestInterceptor)
      axios.interceptors.response.eject(responseInterceptor)
    }
  }, [forceSessionExpired])

  const checkSetToken = () => {
    const existingUser = localStorage.getItem('db_usr')
    const parsedUser = existingUser ? JSON.parse(existingUser) : null
    const existingToken = parsedUser ? parsedUser.token : null
    const isAnonymousPage =
      window.location.pathname === '/request-reset' ||
      window.location.pathname === '/reset-password'

    if (isAnonymousPage) {
      clearStoredSession()
      dispatch({
        type: 'UPDATE_USER',
        payload: {
          item: { token: null, isAuthenticated: false, loading: false },
        },
      })
    } else if (existingToken) {
      validate(existingToken).then(valid => {
        if (valid && existingToken) {
          introspect(existingToken).then(user => {
            identifyUser(user)
            permissions(user.public_id).then(data => {
              loginExpiredNotificationSent = false
              dispatch({
                type: 'LOGIN_USER',
                payload: {
                  ...user,
                  token: existingToken,
                  isAuthenticated: true,
                  permissions: data.data,
                  loading: false,
                },
              })
            })
          })
        } else {
          clearStoredSession()
          dispatch({
            type: 'UPDATE_USER',
            payload: {
              item: { token: null, isAuthenticated: false, loading: false },
            },
          })
          saveAuthReturnPath({
            pathname: window.location.pathname,
            search: window.location.search,
            hash: window.location.hash,
          })
          history.push('/login')
        }
      })
    } else {
      dispatch({
        type: 'UPDATE_USER',
        payload: {
          item: { token: null, isAuthenticated: false, loading: false },
        },
      })
    }
  }

  useEffect(() => {
    if (state.isAuthenticated) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${state.token}`
      if (
        location.pathname.includes('login') ||
        location.pathname.includes('redirect')
      ) {
        const returnPath = resolveAuthReturnPath(locationState?.from)
        history.replace(returnPath)
      }
    }
  }, [
    history,
    location.pathname,
    locationState?.from,
    state.isAuthenticated,
    state.token,
  ])

  useEffect(() => {
    checkSetToken()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: checkSetToken intentionally omitted - should only run once on mount
  }, [])

  return (
    <UserContext.Provider value={[state, dispatch]}>
      {children}
    </UserContext.Provider>
  )
}

export const useUserContext = () => {
  const [state, dispatch] = useContext(UserContext)

  const updateUser = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper item type
    (item: any) => {
      if (item?.token) {
        loginExpiredNotificationSent = false
      }
      dispatch({
        type: UPDATE_USER,
        payload: {
          item,
        },
      })
    },
    [dispatch],
  )

  const resetUser = useCallback(() => {
    clearStoredSession()
    loginExpiredNotificationSent = false
    dispatch({
      type: RESET_USER,
    })
  }, [dispatch])

  return {
    updateUser,
    resetUser,
    state,
  }
}
